import React, { useEffect, useState } from 'react';
import { FaTimes, FaPlay, FaExclamationCircle, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';

// ---------------------------------------------------------------------------
// Full-screen Theater Trailer Modal
// - Opens edge-to-edge (100vh with dvh fallback) like a cinema screen — no boxed frame
// - Auto-requests native fullscreen on supported devices (mobile/tablet/TV)
// - Auto-rotates to landscape on mobile while the trailer is open
// - Esc / Back / Close exits fullscreen + rotation lock and closes
// ---------------------------------------------------------------------------

const isMobileDevice = () =>
  typeof navigator !== 'undefined' &&
  (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '') ||
    (typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches));

const requestOrientationLandscape = async () => {
  try {
    const orientation = typeof window !== 'undefined' && window.screen?.orientation;
    if (orientation?.lock) {
      await orientation.lock('landscape');
      return true;
    }
  } catch {
    /* not supported / denied */
  }
  return false;
};

const unlockOrientation = () => {
  try {
    const orientation = typeof window !== 'undefined' && window.screen?.orientation;
    if (orientation?.unlock) {
      orientation.unlock();
    }
  } catch {
    /* no-op */
  }
};

const requestFullscreenSafe = async () => {
  const el = document.documentElement;
  try {
    if (el.requestFullscreen) return await el.requestFullscreen().catch(() => false);
    if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
  } catch {
    /* not supported */
  }
  return false;
};

const exitFullscreenSafe = () => {
  try {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    }
  } catch {
    /* no-op */
  }
};

export const TrailerModal = ({ isOpen, onClose, activeTrailer }) => {
  // Register a temporary keydown handler so TV Remote / Escape closes cleanly
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      const k = e.key;
      const code = e.keyCode || e.which || 0;
      if (k === 'Escape' || code === 27 || k === 'Backspace' || code === 461 || code === 10009) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isOpen, onClose]);

  // Fullscreen + landscape lock while the trailer plays (mobile-first)
  useEffect(() => {
    if (!isOpen) return undefined;
    let orientationLocked = false;
    const enterImmersive = async () => {
      const isMobile = isMobileDevice();
      const wentFullscreen = await requestFullscreenSafe();
      if (isMobile || wentFullscreen) {
        orientationLocked = await requestOrientationLandscape();
      }
    };
    enterImmersive();
    return () => {
      exitFullscreenSafe();
      if (orientationLocked) unlockOrientation();
    };
  }, [isOpen]);

  const [isMuted, setIsMuted] = useState(false);

  if (!isOpen) return null;

  const movieTitle =
    activeTrailer?.title || activeTrailer?.original_title || activeTrailer?.name || 'Official Trailer';
  const videoKey = activeTrailer?.videoKey;
  const loading = activeTrailer?.loading;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black animate-fade-in"
      // 100dvh breaks on old Android WebViews (invalid unit → height: auto → 0px black frame).
      // Legacy-safe ordering: modern browsers win with the later dvh declaration.
      style={{ height: '100vh' }}
      role="dialog"
      aria-modal="true"
      aria-label={`${movieTitle} trailer player`}
    >
      {/* Close button — floats over the video */}
      <button
        onClick={onClose}
        aria-label="Close trailer"
        className="absolute top-3 right-3 z-20 p-2.5 rounded-full bg-black/60 hover:bg-black/80 text-white border border-white/20 backdrop-blur-md transition-colors"
      >
        <FaTimes className="w-5 h-5" />
      </button>

      {/* Video fills the whole viewport */}
      <div className="absolute inset-0 w-full h-full bg-black">
        {loading ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-gray-300">
            <div className="w-12 h-12 border-2 border-accent-gold border-t-transparent rounded-full animate-spin" />
            <p className="text-xs font-semibold tracking-wide">Loading official trailer…</p>
          </div>
        ) : videoKey ? (
          <>
            <iframe
              title={`${movieTitle} Trailer`}
              src={`https://www.youtube.com/embed/${videoKey}?autoplay=1&rel=0&modestbranding=1&playsinline=1&fs=1&iv_load_policy=3`}
              className="absolute inset-0 w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
            {/* YouTube embed starts unmuted; give a quick mute toggle overlay button */}
            <button
              onClick={() => setIsMuted((m) => !m)}
              aria-label={isMuted ? 'Unmute trailer' : 'Mute trailer'}
              className="absolute bottom-4 right-4 z-20 p-2.5 rounded-full bg-black/60 hover:bg-black/80 text-white border border-white/20 backdrop-blur-md transition-colors"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <FaVolumeMute className="w-4 h-4" /> : <FaVolumeUp className="w-4 h-4" />}
            </button>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center p-8 text-center">
            <div className="max-w-md">
              <FaExclamationCircle className="w-14 h-14 text-accent-gold/60 mx-auto mb-4" />
              <h4 className="text-white font-bold text-lg mb-2">Trailer Not Available</h4>
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                No official trailer is indexed for <span className="text-white">"{movieTitle}"</span> right now.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <a
                  href={`https://www.youtube.com/results?search_query=${encodeURIComponent(
                    movieTitle + ' official trailer'
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-accent-gold hover:bg-accent-goldHover text-dark-900 text-xs font-extrabold px-5 py-2.5 rounded-xl transition-all shadow-md hover:scale-105"
                >
                  <FaPlay className="w-3 h-3" />
                  <span>Search Trailer on YouTube</span>
                </a>
                <button
                  onClick={onClose}
                  className="bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-5 py-2.5 rounded-xl transition-colors border border-white/10"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
